interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformationLike;
}

interface WindowWithIdleCallback {
  requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
}

const warmedURLs = new Set<string>();

export function markPhotoLoaded(url: string): void {
  if (url) warmedURLs.add(url);
}

// Warms the regular HTTP cache only after every currently visible photo has
// loaded or failed. Content-addressed URLs make the remaining responses
// reusable by another attendance link without storing binary data in localStorage.
export function schedulePhotoWarmup(urls: string[], currentVisibleURLs: () => string[] = () => []): () => void {
  const connection = (navigator as NavigatorWithConnection).connection;
  if (connection?.saveData || connection?.effectiveType?.includes("2g")) return () => undefined;

  const pending = [...new Set(urls.filter(Boolean))].filter((url) => !warmedURLs.has(url));
  if (pending.length === 0) return () => undefined;

  let cancelled = false;
  let idleHandle: number | undefined;
  let timer: number | undefined;
  const browserWindow = window as WindowWithIdleCallback;

  const warm = async () => {
    let index = 0;
    const worker = async () => {
      while (!cancelled && index < pending.length) {
        const url = pending[index++];
        if (warmedURLs.has(url)) continue;
        warmedURLs.add(url);
        try {
          const response = await fetch(url, {
            cache: "force-cache",
            credentials: "same-origin",
            priority: "low",
          } as RequestInit & { priority: "low" });
          if (!response.ok) throw new Error(`photo warmup returned ${response.status}`);
          await response.arrayBuffer();
        } catch {
          warmedURLs.delete(url);
        }
      }
    };
    await Promise.all([worker(), worker()]);
  };

  const visiblePhotosReady = () => {
    const visible = [...new Set(currentVisibleURLs().filter(Boolean))];
    return visible.every((url) => warmedURLs.has(url));
  };

  const beginDuringIdle = () => {
    if (cancelled) return;
    if (!visiblePhotosReady()) {
      timer = window.setTimeout(waitForVisiblePhotos, 150);
      return;
    }
    const startIfStillReady = () => {
      if (cancelled) return;
      if (!visiblePhotosReady()) {
        timer = window.setTimeout(waitForVisiblePhotos, 150);
        return;
      }
      void warm();
    };
    if (browserWindow.requestIdleCallback) {
      idleHandle = browserWindow.requestIdleCallback(startIfStillReady, { timeout: 2500 });
    } else {
      startIfStillReady();
    }
  };

  const waitForVisiblePhotos = () => {
    if (cancelled) return;
    if (visiblePhotosReady()) {
      timer = window.setTimeout(beginDuringIdle, 400);
      return;
    }
    timer = window.setTimeout(waitForVisiblePhotos, 150);
  };
  timer = window.setTimeout(waitForVisiblePhotos, 250);

  return () => {
    cancelled = true;
    if (timer !== undefined) window.clearTimeout(timer);
    if (idleHandle !== undefined) browserWindow.cancelIdleCallback?.(idleHandle);
  };
}
